import * as React from 'react';
import {DataGrid, GridActionsCellItem} from '@mui/x-data-grid';
import {useDispatch, useSelector,} from "react-redux";
import {
    EditUser,
    fetchUser,
    setConfig,
} from "../../index";
import {StatusBall} from "../../../../../Components/Elements/StatusBall";
import {setRightDrawer, setModal} from "../../../../Common/App";
import {Avatar, Typography, Tooltip} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import useDeepEqualSelector from "../../../../../Hooks/useDeepEqualSelector";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import {ResendInvitationModal} from "../Modals/ResendInvitationModal";
import {DeleteUser} from "../Modals/DeleteUser";
import {usePermissions} from "../../../../../Hooks/usePermissions";
import {relativeTimeFormatter} from "../../../../../Setup/Config/formats";

export const MainTable = ({isLoading, setIsLoading}) => {

    const theme = useTheme();
    const items = useSelector((store) => store.users.list);
    const departments = useSelector((store) => store.app.config.departments);
    const dispatch = useDispatch();
    const config = useDeepEqualSelector((store) => store.users.config);
    const {hasPermission} = usePermissions();

    const editItem = React.useCallback(
        (id) => async () => {
            await fetchUser({dispatch, filters:{id, with:['roles','department']}}).then((res) => {
                if(res.success){
                    dispatch(setRightDrawer({component:<EditUser />, open:true}));
                }
            });
        },
        [dispatch],
    );

    const viewItem = React.useCallback(
        (id) => async () => {
            await fetchUser({dispatch, filters:{id, with:['roles','department']}}).then((res) => {
                if(res.success){
                    dispatch(setRightDrawer({component:<EditUser />, open:true}));
                }
            });
        },
        [dispatch],
    );

    const handleResendInvitation = React.useCallback(
        (id, userName) => () => {
            dispatch(setModal({component: <ResendInvitationModal userId={id} userName={userName} />, open: true}));
        },
        [dispatch],
    );

    const handleDelete = React.useCallback(
        (id) => () => {
            dispatch(setModal({component: <DeleteUser id={id} />, open: true}));
        },
        [dispatch],
    );

    const columns = React.useMemo(
        () => {
            const colorCode = {
                MANAGER: 'warning',
                EMPLOYEE: 'info',
                ADMIN: 'error',
                SUPER_ADMIN: 'error',
            };

            return [
            { field: 'avatar', headerName:'Avatar', align: 'center', type: 'string', width:80,
                renderCell: (params) => <Avatar sx={{marginTop:1.5}} src={params.row.avatar_url || 'images/user-icon-3.jpg'} alt="A" style={{width: 35, height: 35, borderRadius: '50%', cursor:'pointer'}} onClick={ viewItem( params.id ) } />
            },
            { field: 'name', headerName:'Name', type: 'string', flex:1, minWidth:200,
                renderCell: (params) => {
                    const firstName = params.row.first_name || '';
                    const lastName = params.row.last_name || '';
                    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
                    
                    return (
                        <Typography 
                            sx={{
                                fontSize:'0.86rem', 
                                lineHeight:'inherit', 
                                cursor:'pointer', 
                                textDecoration:'none', 
                                color: theme.text.link
                            }} 
                            onClick={viewItem(params.id)}
                        >
                            {fullName || '-'}
                        </Typography>
                    );
                }
            },
            { field: 'department_id', headerName:'Department', type: 'string', flex:1,
                renderCell: (params) => {
                    const department = departments?.find(d => d.id === params.row.department_id);
                    return (
                        <Typography sx={{fontSize:'0.86rem', lineHeight:'inherit', cursor:'pointer', textDecoration:'none', color: theme.text.link}} onClick={ viewItem( params.id ) }>
                            {department?.department_name || '-'}
                        </Typography>
                    );
                },
            },
            { field: 'email', headerName:'Email', type: 'string', flex:1, minWidth: 220 },
            { field: 'deleted_at', headerName:'Status', type: 'string', flex:1, minWidth: 80,
                renderCell: (params) => ( params.value === null ? <StatusBall label="Active" size="small" color="info" /> : <StatusBall label="Suspend" size="small" color="error" />)
            },
            { field: 'role_type', headerName:'Role Type', type: 'string', flex:1, minWidth: 100,
                renderCell: (params) => ( <StatusBall label={params.value} size="small" color={colorCode[params.value]} /> )
            },
            { field: 'last_login_at', headerName:'Last Login At', type: 'string', flex:1, minWidth:180,
                renderCell: (params) => {
                    const { display, tooltip } = relativeTimeFormatter(params.value);
                    return (
                        <Tooltip title={tooltip || display} arrow>
                            <Typography sx={{ fontSize: '0.86rem!important', lineHeight: 'inherit' }}>
                                {display}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
            {
                field: 'actions',
                type: 'actions',
                headerName:'Action',
                width: 120,
                showInMenu: true,
                getActions: (params) => {
                    const actions = [
                        <GridActionsCellItem
                            key="edit"
                            icon={<EditIcon />}
                            label="Edit"
                            onClick={editItem(params.id)}
                        />,
                    ];
                    
                    // Check if user has completed registration (password setup)
                    if (!params.row.is_registration_completed) {
                        const userName = [params.row.first_name, params.row.last_name].filter(Boolean).join(' ').trim() || params.row.email;
                        actions.push(
                            <GridActionsCellItem
                                key="resend-invitation"
                                icon={<EmailIcon />}
                                label="Resend Invitation"
                                onClick={handleResendInvitation(params.id, userName)}
                            />
                        );
                    }

                    // Add Delete action if user has permission and is not super admin
                    if (hasPermission('core.user.delete') && !params.row.is_super_admin) {
                        actions.push(
                            <GridActionsCellItem
                                key="delete"
                                icon={<DeleteIcon />}
                                label="Delete"
                                onClick={handleDelete(params.id)}
                            />
                        );
                    }
                    
                    return actions;
                },
            },
            ];
        },
        [editItem, handleResendInvitation, handleDelete, hasPermission, departments, theme.text.link, viewItem],
    );
    
    return (
        <DataGrid
            loading={isLoading}
            rows={items || []}
            getRowId={(row) => row.id}
            rowHeight={60}
            columns={columns}
            pagination
            paginationMode="server"
            paginationModel={config.pagination}
            rowCount={config?.total ?? 0}
            pageSizeOptions={[5, 10, 25, 100]}
            onPaginationModelChange={(params) => !isLoading && (setIsLoading(true) || dispatch(setConfig({ pagination: { ...config.pagination, page: params.page, pageSize: params.pageSize } })))}
            initialState={{
                columns:{
                    columnVisibilityModel:{
                        id:false,
                    }
                },
                sorting: {
                    sortModel: [{ field: 'group', sort: 'asc' }],
                },
                pagination: { paginationModel: { ...config.pagination } }
            }}
        />
    );
}
